import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const mandelieuLaNapouleEn: ChauffeurPage = {
  slug: "mandelieu-la-napoule",
  title: "VTC & private chauffeur in Mandelieu-la-Napoule",
  subtitle:
    "Private chauffeur in Mandelieu-la-Napoule: Nice airport transfers, golf courses, the marinas and the Château de la Napoule, 7 days a week.",
  keywords: [
    "vtc mandelieu",
    "private chauffeur mandelieu-la-napoule",
    "mandelieu airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Mandelieu-la-Napoule",
    subtitle:
      "Nice airport transfers, golf courses, marinas and the Château de la Napoule — available 24/7.",
    keywords: [
      "VTC Mandelieu",
      "La Napoule chauffeur",
      "Nice airport transfer",
      "Cannes-Mandelieu golf",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Mandelieu-la-Napoule",
    badges: ["Available 24/7", "Flight tracking", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Mandelieu-la-Napoule",
    items: [
      {
        title: "Nice airport, door to door",
        description:
          "Nice airport around 40 minutes away via the A8, with flight tracking and a personal welcome at the terminal.",
      },
      {
        title: "Golf & marinas",
        description:
          "Old Course Cannes-Mandelieu, the Riviera course, La Rague and La Napoule ports: drop-off as close as possible, no parking worries.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "French/English, punctual and discreet, used to golfing and international guests.",
      },
      {
        title: "Local roots",
        description:
          "Based on the French Riviera, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Mandelieu-la-Napoule",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up with flight tracking to and from Nice Côte d’Azur airport, the Cannes-Mandelieu aerodrome and the stations.",
      },
      {
        title: "Golf & sporting stays",
        text: "Links to the Old Course Cannes-Mandelieu and the Riviera course, with room for golf bags.",
      },
      {
        title: "Chauffeur at your disposal",
        text: "By the hour or the day, a dedicated chauffeur for the Mimosa Route, the Estérel and the hinterland.",
      },
      {
        title: "Ports & yachting",
        text: "Links with La Rague port, La Napoule port and Riviera Marina for crews and guests.",
      },
      {
        title: "Long distance",
        text: "Any-distance trips from Mandelieu: Cannes, Saint-Tropez, Monaco, Italy and ski resorts.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Château de la Napoule",
        description:
          "Waterfront castle and Henry Clews art foundation, facing the marina.",
      },
      {
        name: "La Napoule port & Riviera Marina",
        description: "Marinas at the foot of the town, beaches and seafront.",
      },
      {
        name: "La Rague port",
        description:
          "Port and boatyard on the Théoule side, towards the Estérel.",
      },
      {
        name: "Old Course Cannes-Mandelieu",
        description:
          "One of France’s oldest golf courses, along the Siagne river.",
      },
      {
        name: "Estérel massif",
        description:
          "Red rocks, the San Peyre hill and trails at the edge of town.",
      },
      {
        name: "Nice airport",
        description: "About 40 minutes away via the A8 motorway.",
      },
    ],
  },
  generic: {
    heading: "A private chauffeur across Mandelieu-la-Napoule",
    subheading:
      "From airport transfers to golf days, premium service between the sea and the Estérel.",
    paragraphs: [
      "KS Rent Services handles your journeys with a private chauffeur in Mandelieu-la-Napoule and across the French Riviera: transfers from Nice airport (about 40 minutes), golf days, marina access and long-distance trips.",
      "Our bilingual chauffeurs know Mandelieu — the Château de la Napoule, La Rague and La Napoule ports, the Old Course and the Mimosa Route — to drop you as close as possible, even in high season or during the Mimosa Festival.",
      "Golf in the morning, lunch at the port and an Estérel walk in the afternoon: your chauffeur strings the stops together with no parking or return constraint.",
    ],
    bullets: [
      "Flight tracking & personal welcome",
      "Room for golf bags and luggage",
      "Port & Estérel links",
    ],
  },
  process: {
    heading: "Book a chauffeur in Mandelieu-la-Napoule",
    steps: [
      "Share your route, timings and number of passengers.",
      "We confirm the right vehicle and the fixed fare.",
      "Your chauffeur tracks your flight and greets you on time.",
      "Live adjustments for traffic and any last-minute changes.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Mandelieu-la-Napoule"),
    {
      q: "How long is the transfer to Nice airport from Mandelieu?",
      a: "Around 40 minutes via the A8 motorway in normal conditions. We run transfers 24/7 with flight tracking and a personal welcome.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the Mimosa Festival; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English and used to international guests.",
    },
    {
      q: "Which vehicles do you offer in Mandelieu?",
      a: "Mercedes E-Class and S-Class sedans, 7-seat van, SUV and VIP minibus, with room for luggage and golf bags.",
    },
    {
      q: "Do you drive golfers to the Mandelieu courses?",
      a: "Yes, we serve the Old Course Cannes-Mandelieu and the Riviera course, with a vehicle suited to golf bags and waiting on site if needed.",
    },
    {
      q: "Do you link with the marinas?",
      a: "Yes, we reach La Rague port, La Napoule port and Riviera Marina for crews, owners and guests.",
    },
  ],
  relatedLinks: [
    { label: "Chauffeur service Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Book a chauffeur on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Rent a car Cannes",
      href: "/en/location/car-rental-cannes",
    },
  ],
  cta: {
    title: "Your private chauffeur in Mandelieu-la-Napoule",
    text: "Book your transfer or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Mandelieu-la-Napoule — car service 24/7",
    description:
      "Private car service in Mandelieu-la-Napoule: Nice airport transfer (~40 min), golf, La Rague and La Napoule marinas. 5.0/5 from 38 reviews. Instant quote.",
    canonical: "/en/chauffeur/mandelieu-la-napoule",
  },
};

export default mandelieuLaNapouleEn;
